import { Box, Container, Icon, Link, OrderTypesEnum, SnackBarContext } from '@frontend-workspace/react-shared-lib';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { makeRequest, useGetRequestNewHook } from '../requestUtils';
import SalesOfferSkeleton from './sales-offer-skeleton';
import { PageTitle, SalesOfferCart, SalesOfferProductSelection, SalesOfferSummary, SalesOfferContractSelection, SalesOfferProvider, useSalesOfferContext } from '@frontend-workspace/webkua-lib';
import { IOfferContractsWithProduct } from '../_models/offer-contracts-with-product';
import { IOfferProductGroup } from '../_models/offer-product';
import { IProductGroup, ISalesProposal } from '../_models/product-group';
import { Col, Row } from 'reactstrap';

/* eslint-disable-next-line */
export interface SalesOfferProps {
  products: IOfferProductGroup[],
  proposalId: string,
  data: ISalesProposal
}

export function SalesOffer() {
  const { proposalId } = useParams();
  const { data, isLoading }: { data: ISalesProposal, isLoading: boolean } = useGetRequestNewHook(`proposals/${proposalId}`, {}, []);
  const products: { data: IOfferProductGroup[], getData: () => void, isLoading: boolean } = useGetRequestNewHook('proposals/action/getProductGroups', [], []);
  return (isLoading || products.isLoading || !proposalId) ?
    <SalesOfferSkeleton /> :
    <SalesOfferProvider initialState={data}>
      <SalesOfferContent
        products={products.data}
        proposalId={proposalId}
        data={data}
      />
    </SalesOfferProvider>
}

export function SalesOfferContent(props: SalesOfferProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setSnackbarMessage } = useContext(SnackBarContext);
  const { formData, updateFormData } = useSalesOfferContext();
  const [orderNum, setOrderNum] = useState<string>('');
  const [currentGroup, setCurrentGroup] = useState<IOfferProductGroup>(); //current product group info
  const [currentProductGroup, setCurrentProductGroup] = useState<IProductGroup[]>([]); //list of products in current group
  const [openProductSelection, setOpenProductSelection] = useState<boolean>(false);
  const [openCart, setOpenCart] = useState<boolean>(false);
  const [openContractSelection, setOpenContractSelection] = useState<boolean>(false);
  const [contracts, setContracts] = useState<IOfferContractsWithProduct | null>(null);
  const [transactionInProgress, setTransactionInProgress] = useState<boolean>(false);

  useEffect(() => {
    if (currentGroup) {
      addProductGroupToCart(currentGroup);
    }
  }, [formData.contract]);

  const showProductSelection = () => setOpenProductSelection(true);
  const hideProductSelection = () => setOpenProductSelection(false);
  const showCart = () => setOpenCart(true);
  const hideCart = () => setOpenCart(false);
  const showContractSelection = () => setOpenContractSelection(true);
  const hideContractSelection = () => setOpenContractSelection(false);

  const saveOffer = () => {
    saveOfferRequest().then(result => {
      setSnackbarMessage({ content: t('shop.offerSaved'), type: 'success' });
    });
  };

  const submitOffer = () => {
    setSnackbarMessage({ content: t('shop.sendingOffer'), type: 'info' });
    setTransactionInProgress(true);
    if (formData.customerEmails.length === 0) {
      setSnackbarMessage({ content: t('shop.noProposalRecepientsError'), type: 'error' });
      setTransactionInProgress(false);
      return;
    }
    if (formData.cart.length === 0) {
      setSnackbarMessage({ content: t('shop.noProductsError'), type: 'error' });
      setTransactionInProgress(false);
      return;
    }
    saveOfferRequest().then(result => {
      makeRequest(`proposals/${props.proposalId}/action/send`, "POST").then(result => {
        setSnackbarMessage({ content: t('shop.offerSent'), type: 'success' });
        setTransactionInProgress(false);
        navigate('/overview');
      })
    }).catch(err => {
      setTransactionInProgress(false);
    });
  }

  const acceptCounterProposal = () => {
    setSnackbarMessage({ content: t('shop.acceptingCounterProposal'), type: 'info' });
    setTransactionInProgress(true);
    saveOfferRequest().then(result => {
      makeRequest(`proposals/${props.proposalId}/action/acceptCounterProposal`, "POST").then(result => {
        setSnackbarMessage({ content: t('shop.offerAccepted'), type: 'success' });
        setTransactionInProgress(false);
        navigate('/overview');
      });
    });
  }

  const handlDeleteProposal = () => {
    makeRequest(`proposals/${props.proposalId}`, "DELETE").then(result => {
      setSnackbarMessage({ content: t('shop.proposalDeleted'), type: 'success' });
      navigate('/overview');
    });
  }

  const handleProductSelectionComplete = () => {
    hideProductSelection();
    if (!formData.contract) {
      showContractSelection();
    } else {
      showCart();
    }
  };

  const handleCartSelectionComplete = () => {
    setCurrentGroup(undefined);
    hideCart();
  }

  const handleCartSelectionCancel = () => {
    if (!formData.cart.some(p => p.newSeats > 0)) {
      updateFormData('contract', null);
    }
    setCurrentGroup(undefined);
    hideCart();
    showProductSelection();
  }

  const handleContractSelectionComplete = () => {
    hideContractSelection();
    showCart();
  };

  const getItemsFromCart = () => {
    return formData.cart.filter(item => item.newSeats > 0).map((item) => {
      return {
        itemInfoId: item.itemInfoId,
        quantity: item.newSeats,
        licenseNumber: item.licenseNumber,
        recurringDiscountPercent: item.recurringDiscountPercent,
        oneTimeDiscount: item.oneTimeDiscountAmount,
        allowCustomerSeatAlteration: item.allowCustomerSeatAlteration || false,
        isAutodeskProduct: item.isAutodeskProduct || false
      }
    });
  };

  const saveOfferRequest = () => makeRequest(`proposals/${props.proposalId}`, "PUT", {
    items: getItemsFromCart(),
    contractNumber: formData.contract?.contractNumber || "NY",
    orderNum: orderNum,
    noteToCustomer: formData.noteToCustomer,
    internalNote: formData.internalNote,
    customerEmails: formData.customerEmails,
    expirationDate: formData.expirationDate,
  });

  function handleSelectProductGroup(group: IOfferProductGroup): void {
    setCurrentGroup(group);
    if (formData.contract) {
      addProductGroupToCart(group);
    } else {
      setOpenContractSelection(true);
      makeRequest(`proposals/action/getContractsWithGroup/company/${formData.companyNumber}/group/${group.id}`, 'GET')
        .then((result: IOfferContractsWithProduct) => {
          setContracts(result);
        })
        .catch(err => console.error('get contracts with product error: ', err))
    }
  };

  const addProductGroupToCart = (product: IOfferProductGroup) => {
    if (formData.contract) {
      makeRequest(`proposals/action/getProductsInGroup/company/${formData.companyNumber}/contract/${formData.contract.contractNumber}/group/${product.id}`, 'GET')
        .then((result: IProductGroup[]) => {
          setCurrentProductGroup(result);
          const newProducts: IProductGroup[] = [];

          result.forEach((res) => {
            if (!formData.cart.some((item) => item.itemInfoId === res.itemInfoId)) {
              res.newSeats = 0;
              res.recurringDiscountPercent = 0;
              res.oneTimeDiscountAmount = 0;
              res.indicators = [];
              newProducts.push(res);
            }
          });

          if (newProducts.length > 0) {
            newProducts[newProducts.length - 1].isLastProductInGroup = true;
            const updatedCart = formData.cart.map(product => ({ ...product, indicators: [] }));
            updateFormData('cart', [...updatedCart, ...newProducts]);
          }

          handleProductSelectionComplete();
        })
        .catch(err => console.error('get product group error: ', err))
        .finally(() => closeContractSelection())
    }
  };

  const closeContractSelection = () => {
    setOpenContractSelection(false);
  };

  const handleBackNavigation = () => navigate(`/overview`);

  return <>
    <Container
      className={`container-fluid pt-4 px-0`}
      maxWidth={false}
      sx={{ paddingBottom: '50px' }}>
      <Row>
        <Col>
          <Box
            component="div"
            className="pb-5">
            <Link onClick={handleBackNavigation} className='d-inline-flex mb-3' underline='hover'>
              <Icon className='me-1'>arrow_back</Icon>{t('standard.overview')}
            </Link>
            <PageTitle title={t("shop.offer")} subtitle={`(${t(`proposalState.${formData.state}`) as string})`} />
            <SalesOfferSummary
              orderNum={orderNum}
              transactionInProgress={transactionInProgress}
              onCancel={() => { }}
              onSetCurrentProductGroup={setCurrentProductGroup}
              onSetOrderNum={setOrderNum}
              onSaveOffer={saveOffer}
              onSubmitOffer={submitOffer}
              onAcceptCounterProposal={acceptCounterProposal}
              onDeleteProposal={handlDeleteProposal}
              onShowProductSelection={showProductSelection}
              data={props.data}
            />

            {/* <PrimaryButton
                  variant="outlined"
                  onClick={showProductSelection}
                  className="minButtonWidth me-3 my-2">
                  {t("shop.addProduct")}
              </PrimaryButton> 
            */}

            <SalesOfferProductSelection
              open={openProductSelection}
              onClose={hideProductSelection}
              onComplete={handleProductSelectionComplete}
              groups={props.products}
              onSetCurrentProductGroup={setCurrentProductGroup}
              onSelectProductGroup={handleSelectProductGroup}
            />
            <SalesOfferCart
              open={openCart}
              onComplete={handleCartSelectionComplete}
              currentGroup={currentGroup}
              currentProductGroup={currentProductGroup}
              onCancel={handleCartSelectionCancel}
            />
            <SalesOfferContractSelection
              open={openContractSelection}
              onClose={hideContractSelection}
              onComplete={handleContractSelectionComplete}
              contracts={contracts}
            />
          </Box>
        </Col>
      </Row>
    </Container>
  </>
}

export default SalesOffer;
